import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { 
  Plus, 
  MagnifyingGlass, 
  Faders,
  Books,
  PencilSimple,
  Trash,
  CheckCircle,
  Clock,
  Archive
} from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { da } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const styles = {
    approved: "status-approved",
    draft: "status-draft",
    retired: "status-retired"
  };
  
  const labels = {
    approved: "Godkendt",
    draft: "Kladde",
    retired: "Arkiveret"
  };

  const icons = {
    approved: CheckCircle,
    draft: Clock,
    retired: Archive
  };

  const Icon = icons[status] || Clock;

  return (
    <span className={styles[status] || styles.draft}>
      <Icon className="w-3 h-3 mr-1" weight="bold" />
      {labels[status] || status}
    </span>
  );
};

export const KnowledgeBasePage = ({ user, setUser }) => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterSite, setFilterSite] = useState("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchArticles();
    fetchCategories();
    fetchSites();
  }, []);

  const fetchArticles = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterSite !== 'all') params.append('site_key', filterSite);

      const response = await fetch(`${API}/knowledge-articles?${params}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      toast.error("Kunne ikke hente artikler");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/knowledge-articles/categories`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await fetch(`${API}/sites`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSites(data);
      }
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchArticles();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search, filterCategory, filterStatus, filterSite]);

  const handleDelete = async () => {
    if (!selectedArticle) return;

    try {
      const response = await fetch(`${API}/knowledge-articles/${selectedArticle.article_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Artikel slettet");
        setDeleteDialogOpen(false);
        setSelectedArticle(null);
        fetchArticles();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Kunne ikke slette artikel");
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Der opstod en fejl");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return format(new Date(dateStr), "d. MMM yyyy", { locale: da });
    } catch {
      return "-";
    }
  };

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-6 md:p-8" data-testid="knowledge-base-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Vidensbase
            </h1>
            <p className="text-slate-600">Administrer artikler som chatbotten bruger til at svare</p>
          </div>
          <Button 
            onClick={() => navigate('/knowledge-base/new')}
            className="btn-primary"
            data-testid="create-article-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ny artikel
          </Button>
        </div>

        {/* Filters */}
        <Card className="card-default mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Søg i artikler..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <div className="flex gap-3">
                <Select value={filterSite} onValueChange={setFilterSite}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-site">
                    <SelectValue placeholder="Alle sites" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle sites</SelectItem>
                    {sites.map(site => (
                      <SelectItem key={site.site_key} value={site.site_key}>
                        {site.brand_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-[160px]" data-testid="filter-category">
                    <SelectValue placeholder="Alle kategorier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle kategorier</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[140px]" data-testid="filter-status">
                    <SelectValue placeholder="Alle status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle status</SelectItem>
                    <SelectItem value="approved">Godkendt</SelectItem>
                    <SelectItem value="draft">Kladde</SelectItem>
                    <SelectItem value="retired">Arkiveret</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Articles Table */}
        {loading ? (
          <Card className="card-default">
            <CardContent className="p-8">
              <div className="animate-pulse space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-12 bg-slate-100 rounded" />
                ))}
              </div>
            </CardContent>
          </Card>
        ) : articles.length === 0 ? (
          <Card className="card-default">
            <CardContent className="p-12 text-center">
              <Books className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="duotone" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Ingen artikler fundet</h3>
              <p className="text-slate-600 mb-4">
                {search || filterCategory !== 'all' || filterStatus !== 'all' 
                  ? "Prøv at ændre dine filtre"
                  : "Opret din første artikel for at komme i gang"}
              </p>
              {!search && filterCategory === 'all' && filterStatus === 'all' && (
                <Button onClick={() => navigate('/knowledge-base/new')} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Opret artikel
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="card-default overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Titel</TableHead>
                  <TableHead className="font-semibold">Kategori</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Opdateret</TableHead>
                  <TableHead className="font-semibold text-right">Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articles.map((article, index) => (
                  <motion.tr
                    key={article.article_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.02 }}
                    className="table-row-hover cursor-pointer"
                    onClick={() => navigate(`/knowledge-base/${article.article_id}`)}
                    data-testid={`article-row-${article.article_id}`}
                  >
                    <TableCell className="font-medium text-slate-900">
                      <div className="max-w-md">
                        <p className="truncate">{article.title}</p>
                        {article.product_tags?.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {article.product_tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{article.category}</TableCell>
                    <TableCell>
                      <StatusBadge status={article.status} />
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(article.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => navigate(`/knowledge-base/${article.article_id}`)}
                          data-testid={`edit-article-${article.article_id}`}
                        >
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                        {user?.role === 'admin' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setSelectedArticle(article);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-article-${article.article_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </motion.tr>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Er du sikker?</AlertDialogTitle>
              <AlertDialogDescription>
                Dette vil permanent slette artiklen "{selectedArticle?.title}" og dens versionshistorik.
                Denne handling kan ikke fortrydes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuller</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-article-btn"
              >
                Slet artikel
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};
